import React, { useState, useEffect } from "react";
import { FiEye, FiEyeOff } from "../../lib/icons";
import { useFocusMode } from "../../contexts/FocusModeContext";

interface FocusModeToggleProps {
  size?: "small" | "medium" | "large";
  showLabel?: boolean;
  showKeyboardShortcut?: boolean;
  className?: string;
}

export const FocusModeToggle: React.FC<FocusModeToggleProps> = ({
  size = "medium",
  showLabel = true,
  showKeyboardShortcut = true,
  className = "",
}) => {
  const { isFocusModeActive, toggleFocusMode } = useFocusMode();
  const [showNotification, setShowNotification] = useState(false);

  // Show notification when focus mode toggles
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => setShowNotification(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const handleToggle = () => {
    toggleFocusMode();
    setShowNotification(true);
  };

  const sizeClasses = {
    small: "px-2 py-1 text-xs",
    medium: "px-3 py-1.5 text-sm",
    large: "px-4 py-2 text-base",
  };

  const iconSizes = {
    small: "w-3 h-3",
    medium: "w-4 h-4",
    large: "w-5 h-5",
  };

  const Icon = isFocusModeActive ? FiEyeOff : FiEye;

  return (
    <>
      <button
        onClick={handleToggle}
        className={`
          focus-mode-toggle
          ${isFocusModeActive ? "active" : ""}
          ${sizeClasses[size]}
          ${className}
        `.trim()}
        title={`${isFocusModeActive ? "Exit" : "Enter"} Focus Mode (Ctrl/Cmd+F)`}
        aria-label={`${isFocusModeActive ? "Exit" : "Enter"} focus mode`}
        aria-pressed={isFocusModeActive}
      >
        <Icon className={`focus-icon ${iconSizes[size]}`} />
        {showLabel && <span>{isFocusModeActive ? "Exit Focus" : "Focus Mode"}</span>}
        {showKeyboardShortcut && <span className="keyboard-shortcut">⌘F</span>}
      </button>

      {/* Notification */}
      {showNotification && (
        <div
          className={`focus-mode-notification ${showNotification ? "" : "exiting"}`}
          role="status"
          aria-live="polite"
        >
          {isFocusModeActive
            ? "🎯 Focus Mode activated - distractions minimized"
            : "👁️ Focus Mode deactivated - full interface restored"}
        </div>
      )}

      {/* Focus mode indicator (only shown when active) */}
      {isFocusModeActive && <div className="focus-mode-indicator">🎯 FOCUS</div>}
    </>
  );
};

export default FocusModeToggle;
